import React, { useState, useMemo, useCallback, useEffect } from 'react';
import soaData from '../SOA.json';
import { ChevronRight, ChevronDown, Check, Minus, Search, X, Building2 } from 'lucide-react';

/**
 * Componente per la selezione delle categorie SOA (Società Organismi di Attestazione).
 * Permette di selezionare categorie SOA e ottenere i codici CPV associati.
 */
const SOASelector = ({ 
  selectedSOACodes = [], 
  onSOACodesChange,
  maxHeight = '400px',
  className = '',
  forceExpanded = false,
  disabled = false
}) => {
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  // Debounce ricerca per evitare filtro/re-render ad ogni keypress
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  // Permette al parent (es. modal guest) di forzare l'espansione
  useEffect(() => {
    if (forceExpanded) setIsExpanded(true);
  }, [forceExpanded]);

  const selectedSOASet = useMemo(() => new Set(selectedSOACodes), [selectedSOACodes]);

  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput), 200);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Trasforma i dati SOA in una struttura ad albero
  const soaTree = useMemo(() => {
    // Raggruppa per tipo (OG = Opere Generali, OS = Opere Speciali)
    const ogCategories = [];
    const osCategories = [];

    Object.entries(soaData).forEach(([code, data]) => {
      if (data && data.descrizione) {
        const category = {
          code,
          descrizione: data.descrizione,
          cpvCodes: (data.cpv || []).map(c => c.codice.split('-')[0])
        };

        if (code.startsWith('OG')) {
          ogCategories.push(category);
        } else if (code.startsWith('OS')) {
          osCategories.push(category);
        }
      }
    });

    // Ordina per numero
    const sortByCode = (a, b) => {
      const numA = parseInt(a.code.replace(/\D/g, ''));
      const numB = parseInt(b.code.replace(/\D/g, ''));
      return numA - numB;
    };

    ogCategories.sort(sortByCode);
    osCategories.sort(sortByCode);

    return [
      {
        code: 'OG',
        descrizione: 'OPERE GENERALI',
        children: ogCategories,
        isGroup: true
      },
      {
        code: 'OS',
        descrizione: 'OPERE SPECIALI',
        children: osCategories,
        isGroup: true
      }
    ];
  }, []);

  // Ottieni tutti i codici CPV associati a una categoria SOA
  const getCpvCodesForSOA = useCallback((soaCode) => {
    const data = soaData[soaCode];
    if (data && data.cpv) {
      return data.cpv.map(c => c.codice.split('-')[0]);
    }
    return [];
  }, []);

  // Verifica se una categoria SOA è selezionata
  const isSOASelected = useCallback((soaCode) => {
    return selectedSOASet.has(soaCode);
  }, [selectedSOASet]);

  // Verifica se tutti i figli di un gruppo sono selezionati
  const areAllChildrenSelected = useCallback((group) => {
    if (!group.children || group.children.length === 0) return false;
    return group.children.every(child => selectedSOASet.has(child.code));
  }, [selectedSOASet]);

  // Verifica se alcuni figli di un gruppo sono selezionati
  const areSomeChildrenSelected = useCallback((group) => {
    if (!group.children || group.children.length === 0) return false;
    return group.children.some(child => selectedSOASet.has(child.code));
  }, [selectedSOASet]);

  // Gestisce il toggle di una categoria SOA
  const handleSOAToggle = useCallback((node) => {
    if (node.isGroup) {
      // È un gruppo (OG o OS): seleziona/deseleziona tutti i figli
      const childCodes = node.children.map(c => c.code);
      
      if (areAllChildrenSelected(node)) {
        // Deseleziona tutti
        const newCodes = selectedSOACodes.filter(code => !childCodes.includes(code));
        onSOACodesChange(newCodes);
      } else {
        // Seleziona tutti
        const newCodes = [...new Set([...selectedSOACodes, ...childCodes])];
        onSOACodesChange(newCodes);
      }
    } else {
      // È una singola categoria SOA
      if (selectedSOACodes.includes(node.code)) {
        onSOACodesChange(selectedSOACodes.filter(c => c !== node.code));
      } else {
        onSOACodesChange([...selectedSOACodes, node.code]);
      }
    }
  }, [selectedSOACodes, onSOACodesChange, areAllChildrenSelected]);

  // Toggle espansione nodo
  const toggleExpand = useCallback((nodeCode) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeCode)) {
        newSet.delete(nodeCode);
      } else {
        newSet.add(nodeCode);
      }
      return newSet;
    });
  }, []);

  // Filtra l'albero in base al termine di ricerca
  const filteredTree = useMemo(() => {
    if (!searchTerm) return soaTree;
    
    const lowerTerm = searchTerm.toLowerCase();
    
    return soaTree.map(group => {
      const filteredChildren = group.children.filter(child => 
        child.code.toLowerCase().includes(lowerTerm) ||
        child.descrizione.toLowerCase().includes(lowerTerm)
      );
      
      if (filteredChildren.length > 0 || group.descrizione.toLowerCase().includes(lowerTerm)) {
        return {
          ...group,
          children: filteredChildren.length > 0 ? filteredChildren : group.children
        };
      }
      return null;
    }).filter(Boolean);
  }, [soaTree, searchTerm]);

  // Renderizza un nodo dell'albero
  const renderNode = useCallback((node, depth = 0) => {
    const nodeCode = node.code;
    const hasChildren = node.children && node.children.length > 0;
    const isNodeExpanded = expandedNodes.has(nodeCode);
    
    let isSelected, isIndeterminate;
    
    if (node.isGroup) {
      isSelected = areAllChildrenSelected(node);
      isIndeterminate = !isSelected && areSomeChildrenSelected(node);
    } else {
      isSelected = isSOASelected(nodeCode);
      isIndeterminate = false;
    }
    
    // Colori per tipo
    const getNodeColor = () => {
      if (node.isGroup) {
        return node.code === 'OG' 
          ? 'bg-amber-100 text-amber-800 border-amber-300'
          : 'bg-teal-100 text-teal-800 border-teal-300';
      }
      return node.code.startsWith('OG')
        ? 'bg-amber-50 text-amber-700 border-amber-200'
        : 'bg-teal-50 text-teal-700 border-teal-200';
    };
    
    return (
      <div key={nodeCode} className="select-none">
        <div 
          className={`flex items-center py-2 px-2 hover:bg-gray-50 rounded cursor-pointer transition-colors ${
            isSelected ? 'bg-amber-50' : ''
          }`}
          style={{ paddingLeft: `${depth * 20 + 8}px` }}
        >
          {/* Expand/Collapse button */}
          {hasChildren ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(nodeCode);
              }}
              className="w-5 h-5 flex items-center justify-center mr-1 text-gray-500 hover:text-gray-700"
            >
              {isNodeExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
          ) : (
            <span className="w-5 mr-1" />
          )}
          
          {/* Checkbox */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleSOAToggle(node);
            }}
            className={`w-5 h-5 flex items-center justify-center mr-2 rounded border ${
              isSelected 
                ? 'bg-amber-600 border-amber-600 text-white' 
                : isIndeterminate 
                  ? 'bg-amber-200 border-amber-400 text-amber-600'
                  : 'border-gray-300 hover:border-amber-400'
            }`}
          >
            {isSelected ? (
              <Check size={14} />
            ) : isIndeterminate ? (
              <Minus size={14} />
            ) : null}
          </button>
          
          {/* Label */}
          <div 
            className="flex-1 flex items-center gap-2 min-w-0"
            onClick={() => hasChildren && toggleExpand(nodeCode)}
          >
            <span className={`text-xs px-2 py-0.5 rounded border font-semibold ${getNodeColor()}`}>
              {nodeCode}
            </span>
            <span className={`text-sm text-gray-700 ${node.isGroup ? 'font-semibold' : ''}`} title={node.descrizione}>
              {node.descrizione}
            </span>
            {!node.isGroup && (
              <span className="text-xs text-gray-400">
                ({getCpvCodesForSOA(nodeCode).length} CPV)
              </span>
            )}
          </div>
        </div>
        
        {/* Children */}
        {hasChildren && isNodeExpanded && (
          <div className="ml-2">
            {node.children.map(child => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  }, [expandedNodes, toggleExpand, handleSOAToggle, isSOASelected, areAllChildrenSelected, areSomeChildrenSelected, getCpvCodesForSOA]);

  // Calcola i codici CPV totali selezionati
  const totalCpvCodes = useMemo(() => {
    const allCodes = new Set();
    selectedSOACodes.forEach(soaCode => {
      getCpvCodesForSOA(soaCode).forEach(cpv => allCodes.add(cpv));
    });
    return allCodes.size;
  }, [selectedSOACodes, getCpvCodesForSOA]);

  return (
    <div className={`soa-selector ${className} ${disabled ? 'opacity-75' : ''}`}>
      {/* Header con toggle */}
      <div 
        className="flex items-center justify-between p-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors"
        onClick={() => {
          if (disabled) return;
          if (!forceExpanded) setIsExpanded(!isExpanded);
        }}
      >
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-amber-600" />
          <span className="font-medium text-gray-800">Filtro per Categoria SOA</span>
          {selectedSOACodes.length > 0 && (
            <span className="bg-amber-600 text-white text-xs px-2 py-0.5 rounded-full">
              {selectedSOACodes.length} SOA ({totalCpvCodes} CPV)
            </span>
          )}
        </div>
        <ChevronDown 
          className={`h-5 w-5 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
        />
      </div>

      {/* Contenuto espandibile */}
      {isExpanded && (
        <div className={`mt-2 border border-gray-200 rounded-lg bg-white shadow-sm ${disabled ? 'pointer-events-none select-none' : ''}`}>
          {/* Barra di ricerca */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Cerca categoria SOA (es: OG1, edifici, impianti...)"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                disabled={disabled}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => setSearchInput('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Albero SOA */}
          <div 
            className="overflow-y-auto p-2"
            style={{ maxHeight }}
          >
            {filteredTree.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Building2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Nessuna categoria SOA trovata</p>
              </div>
            ) : (
              filteredTree.map(node => renderNode(node))
            )}
          </div>

          {/* Legenda */}
          <div className="p-3 border-t border-gray-200 bg-gray-50">
            <div className="text-xs text-gray-500 mb-2">Legenda categorie:</div>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 font-semibold">OG - Opere Generali</span>
              <span className="px-2 py-0.5 rounded bg-teal-100 text-teal-800 border border-teal-300 font-semibold">OS - Opere Speciali</span>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Le categorie SOA raggruppano codici CPV correlati per tipologia di lavoro.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SOASelector;

